import React, { useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { sessionsAPI } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import { Users } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function GameJoin() {
  const { pin: urlPin } = useParams();
  useSEO('Join Game | JepPrep', 'Enter a game code to join a live multiplayer trivia session.', { canonical: 'https://jepprep.com/join' });
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [pin, setPin] = useState(urlPin || '');
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [isJoining, setIsJoining] = useState(false);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!pin || pin.length !== 6) {
      toast.error('Please enter a valid 6-digit PIN');
      return;
    }
    if (!displayName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setIsJoining(true);
    try {
      const response = await sessionsAPI.join({
        pin,
        display_name: displayName,
        user_id: user?.id
      });
      toast.success('Joined game!');
      navigate(`/game/${response.data.session_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join game');
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
        <CardHeader className="text-center">
          <img src={LOGO_URL} alt="JepPrep" className="h-12 mx-auto mb-4" />
          <div className="w-16 h-16 mx-auto bg-[hsl(var(--brand-blue-800))] rounded-xl flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-[hsl(var(--brand-gold-500))]" />
          </div>
          <CardTitle className="text-2xl font-display text-white">Join Game</CardTitle>
          <CardDescription className="text-gray-400">
            Enter the game PIN to join
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleJoin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pin" className="text-gray-300">Game PIN</Label>
              <Input
                id="pin"
                type="text"
                placeholder="000000"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-center text-2xl font-mono tracking-widest py-6"
                data-testid="join-pin-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="displayName" className="text-gray-300">Your Name</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="Enter your name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white"
                data-testid="join-name-input"
              />
            </div>
            <Button
              type="submit"
              disabled={isJoining || pin.length !== 6 || !displayName.trim()}
              className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg"
              data-testid="join-game-button"
            >
              {isJoining ? 'Joining...' : 'Join Game'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
